import { bffFetch } from "@/shared/api/client";
import { isReadUnavailableStatus } from "@/shared/api/session-fault";
import { riskBandKey } from "@/domain/quant/risk-bands";
import {
  assetClassDistributionQuery,
  toDistributionLevels,
  type AssetClassDistributionWire,
} from "@/domain/clients/asset-class-distribution";
import { readCorporationClientSubject } from "@/domain/clients/corporation-client";
import { individualIsKnown } from "@/domain/clients/individuals";
import type {
  AssetClassDistributionByPortfolio,
  AssetClassDistributionOutcome,
  ClientDetail,
  ClientPortfolio,
} from "@/domain/clients/types";
import { mapWithConcurrency } from "@/shared/concurrency";
import { getAssetClassResolver } from "@/domain/quant/asset-classes";
import { aggregate, toPortfolio, type QuantPortfolioWire } from "@/domain/quant/portfolio-mapping";
import { fetchQuantPortfolios, fetchQuantPortfoliosBy } from "@/domain/quant/quant-portfolios";

export function deriveDobAge(personId: string): { dob: string; age: number } | null {
  if (!personId || personId === "-") return null;
  let digits = personId.replace(/\D/g, "");
  if (digits.length === 10) {
    const yy = Number(digits.slice(0, 2));
    const currentYY = new Date().getUTCFullYear() % 100;
    digits = `${yy > currentYY ? "19" : "20"}${digits}`;
  }
  if (digits.length < 8) return null;
  const y = Number(digits.slice(0, 4));
  const m = Number(digits.slice(4, 6));
  const d = Number(digits.slice(6, 8));
  const nowYear = new Date().getUTCFullYear();
  if (y < 1900 || y > nowYear || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dob = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dob.getTime()) || dob.getUTCMonth() !== m - 1) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - y;
  const todayMD = (now.getUTCMonth() + 1) * 100 + now.getUTCDate();
  if (todayMD < m * 100 + d) age -= 1;
  return { dob: dob.toISOString(), age };
}

export interface ClientPortfolioSummary {
  portfolios: ClientPortfolio[];
  totalValue: number | null;
  returnPct: number | null;
  volatilityPct: number | null;
  cvarPct: number | null;
  riskBandKey: string | null;
}

export async function fetchPortfolioSummary(
  individualId: string,
): Promise<ClientPortfolioSummary | null> {
  return summariseQuantPortfolios(fetchQuantPortfolios(individualId));
}

export async function fetchCorporatePortfolioSummary(
  corporationClientId: string,
): Promise<ClientPortfolioSummary | null> {
  return summariseQuantPortfolios(
    fetchQuantPortfoliosBy({ filter: "corporation_client_id", clientId: corporationClientId }),
  );
}

async function summariseQuantPortfolios(
  rawPromise: Promise<QuantPortfolioWire[]>,
): Promise<ClientPortfolioSummary | null> {
  const raw = await rawPromise;
  const classes = await getAssetClassResolver(
    raw.map((row) =>
      row.quant_asset_class_map_id != null ? String(row.quant_asset_class_map_id) : "",
    ),
  );
  const portfolios = raw
    .map((row) => toPortfolio(row, classes))
    .filter((p): p is ClientPortfolio => p !== null)
    .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));
  if (portfolios.length === 0) return null;

  const agg = aggregate(portfolios);

  for (const p of portfolios) {
    for (const h of p.holdings) {
      h.weightPct =
        agg.totalValue !== null && agg.totalValue > 0 && h.value !== null
          ? (h.value / agg.totalValue) * 100
          : null;
    }
  }

  return {
    portfolios,
    ...agg,
    riskBandKey: agg.volatilityPct === null ? null : riskBandKey(agg.volatilityPct),
  };
}

export async function fetchClientDetail(individualId: string): Promise<ClientDetail | null> {
  const raw = fetchQuantPortfolios(individualId);
  const [known, detail] = await Promise.all([individualIsKnown(individualId), detailOf(raw)]);
  return known ? detail : null;
}

export async function fetchCorporateClientDetail(
  corporationClientId: string,
): Promise<ClientDetail | null> {
  const subject = await readCorporationClientSubject(corporationClientId);
  if (!subject) return null;
  return detailOf(
    fetchQuantPortfoliosBy({
      filter: "corporation_client_id",
      clientId: subject.corporationClientId,
    }),
  );
}

async function detailOf(raw: Promise<QuantPortfolioWire[]>): Promise<ClientDetail | null> {
  const [summary, assetClassDistribution] = await Promise.all([
    summariseQuantPortfolios(raw),
    raw.then((rows) =>
      fetchAssetClassDistributions(
        rows.flatMap((row) => {
          const portfolio = toPortfolio(row);
          return portfolio ? [portfolio.id] : [];
        }),
      ),
    ),
  ]);
  return summary ? { portfolios: summary.portfolios, assetClassDistribution } : null;
}

const DISTRIBUTION_FANOUT_CONCURRENCY = 4;

async function fetchAssetClassDistributions(
  portfolioIds: readonly string[],
): Promise<AssetClassDistributionByPortfolio> {
  const outcomes = await mapWithConcurrency(
    portfolioIds,
    DISTRIBUTION_FANOUT_CONCURRENCY,
    async (id): Promise<AssetClassDistributionOutcome> => {
      const path =
        `v1/quant-portfolio/${encodeURIComponent(id)}` +
        `/asset-class-distribution${assetClassDistributionQuery()}`;
      try {
        const res = await bffFetch(path);
        if (!res.ok) {
          if (isReadUnavailableStatus(res.status)) {
            console.warn(
              `asset-class-distribution: portfolio ${id} read unavailable (${res.status})`,
            );
            return { kind: "unavailable" };
          }
          return { kind: "absent" };
        }
        const wire = (await res.json()) as AssetClassDistributionWire;
        const levels = toDistributionLevels(wire);
        return levels
          ? { kind: "ok", slices: levels.role, classSlices: levels.class }
          : { kind: "absent" };
      } catch (err) {
        console.warn(
          `asset-class-distribution: portfolio ${id} read failed (${err instanceof Error ? err.message : "unknown"})`,
        );
        return { kind: "unavailable" };
      }
    },
  );
  const byPortfolio: AssetClassDistributionByPortfolio = {};
  portfolioIds.forEach((id, i) => {
    byPortfolio[id] = outcomes[i];
  });
  return byPortfolio;
}
